import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';

import { VersionedRouterLink } from '#/components/VersionedLink/VersionedRouterLink';
import { clickActionsAttributes } from '#/snippets/click-actions.attributes';
import { IDEALIST_UTM_PARAMS } from '#/utils/constants/general/idealistUtmParams';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';
import { hasUTMParam } from '#/utils/url/hasUTMParam';
import { isExternalUrl } from '#/utils/url/isExternalUrl';
import { pathWithParams } from '#/utils/url/pathWithParams';

import type { ButtonOrLinkDataProps } from './ButtonOrLinkData';

export type ButtonOrLinkOwnProps = {
  id?: string;
  className?: string;
  children?: ReactNode;
  style?: CSSProperties;
  tabIndex?: number;
  title?: string;
  qaId?: string;
};

type Props = ButtonOrLinkDataProps<ButtonOrLinkOwnProps>;

/**
 * This component is a switch between the multiple types of link that we have
 *
 * It allows using the same style regardless of the target element being a button,
 * an external link, or a react-router link
 */
export function ButtonOrLink({
  id,
  className,
  children,
  style,
  title,
  tabIndex,
  qaId,
  ...otherProps
}: Props) {
  const data = useMemo(() => {
    if (!('data' in otherProps)) return otherProps;

    const { data: otherPropsData, ...remainingOtherProps } = otherProps;
    return { ...otherPropsData, ...remainingOtherProps };
  }, [otherProps]);

  const sharedProps = {
    id,
    className,
    style,
    title,
    tabIndex,
    ...getDataAndAriaProps(otherProps),
  };

  switch (data.type) {
    case 'inert':
      return (
        <div
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          data-qa-id={qaId}
        >
          {children}
        </div>
      );
    case 'button':
      return (
        <button
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          type="button"
          onClick={data.onClick}
          disabled={data.disabled}
          onBlur={data.onBlur}
          onFocus={data.onFocus}
          onMouseOver={data.onMouseOver}
          onMouseOut={data.onMouseOut}
          onTouchStart={data.onTouchStart}
          onTouchMove={data.onTouchMove}
          onTouchEnd={data.onTouchEnd}
          data-qa-id={qaId}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...clickActionsAttributes(data.clickActions)}
        >
          {children}
        </button>
      );
    case 'submit':
      return (
        <button
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          type="submit"
          onClick={data.onClick}
          disabled={data.disabled}
          onBlur={data.onBlur}
          onFocus={data.onFocus}
          onMouseOver={data.onMouseOver}
          onMouseOut={data.onMouseOut}
          onTouchStart={data.onTouchStart}
          onTouchMove={data.onTouchMove}
          onTouchEnd={data.onTouchEnd}
          data-qa-id={qaId}
          data-qa-submitting={data['data-qa-submitting']}
        >
          {children}
        </button>
      );
    case 'link-with-state':
      return (
        <VersionedRouterLink
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...clickActionsAttributes(data.clickActions)}
          to={data.to}
          preventScrollReset={data.preventScrollReset}
          defaultShouldRevalidate={data.defaultShouldRevalidate}
          state={data.state}
          onClick={data.onClick}
          qaId={qaId}
        >
          {children}
        </VersionedRouterLink>
      );
    case 'link': {
      const external = isExternalUrl(data.href);

      if (
        data.anchorType === 'native' ||
        data.href.startsWith('#') ||
        data.target ||
        data.download ||
        external
      ) {
        return (
          <a
            // oxlint-disable-next-line react/jsx-props-no-spreading
            {...sharedProps}
            // oxlint-disable-next-line react/jsx-props-no-spreading
            {...clickActionsAttributes(data.clickActions)}
            href={
              external && !hasUTMParam(data.href)
                ? pathWithParams(data.href, IDEALIST_UTM_PARAMS)
                : data.href
            }
            target={external ? '_blank' : data.target}
            onClick={data.onClick}
            download={data.download}
            rel={external ? 'noopener noreferrer' : undefined}
            data-qa-id={qaId}
          >
            {children}
          </a>
        );
      }

      return (
        <VersionedRouterLink
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...sharedProps}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...clickActionsAttributes(data.clickActions)}
          to={data.href}
          preventScrollReset={data.preventScrollReset}
          defaultShouldRevalidate={data.defaultShouldRevalidate}
          onClick={data.onClick}
          qaId={qaId}
          navLink={data.anchorType === 'nav'}
          end={data.end}
        >
          {children}
        </VersionedRouterLink>
      );
    }
  }
}
